// ============================================================================
// ACTION PLAN PAGE - With i18n Translation Support
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Target, ChevronDown, ChevronRight, CheckCircle, Circle, Clock, 
  BookOpen, Wrench, Users, Brain, ExternalLink, Calendar, Trophy,
  Play, Bookmark, TrendingUp, ArrowLeft, LayoutDashboard, Crown
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import { supabase } from '../lib/supabase';

// ============================================================================
// RESOURCES (Not translated - URLs are universal)
// ============================================================================
const RESOURCES = {
  courses: {
    aiBasics: { name: "AI For Everyone", platform: "Coursera", url: "https://www.coursera.org/learn/ai-for-everyone", duration: "4 weeks" },
    promptEngineering: { name: "ChatGPT Prompt Engineering", platform: "DeepLearning.AI", url: "https://www.deeplearning.ai/short-courses/chatgpt-prompt-engineering-for-developers/", duration: "1 hour" }
  },
  tools: {
    chatgpt: { name: "ChatGPT", url: "https://chat.openai.com" },
    claude: { name: "Claude", url: "https://claude.ai" },
    gemini: { name: "Google Gemini", url: "https://gemini.google.com" },
    perplexity: { name: "Perplexity AI", url: "https://www.perplexity.ai" },
    notion: { name: "Notion AI", url: "https://www.notion.so" },
    gamma: { name: "Gamma", url: "https://gamma.app" }
  }
};

// ============================================================================
// INDUSTRY RESOURCES (Industry names use translation keys)
// ============================================================================
const INDUSTRY_RESOURCES = {
  technology_it: {
    nameKey: "technology_it",
    tools: ["GitHub Copilot", "Cursor AI", "Tabnine", "Amazon CodeWhisperer"],
    courses: [{ name: "AI for Software Development", url: "https://www.coursera.org/learn/generative-ai-for-software-development" }],
    communities: ["r/MachineLearning", "Hacker News", "Dev.to AI"],
    certifications: ["AWS AI Practitioner", "Google Cloud AI", "Azure AI Fundamentals"]
  },
  healthcare: {
    nameKey: "healthcare",
    tools: ["Nuance DAX", "Glass Health", "Viz.ai"],
    courses: [{ name: "AI in Healthcare", url: "https://www.coursera.org/specializations/ai-healthcare" }],
    communities: ["Health AI LinkedIn", "AMIA"],
    certifications: ["Healthcare AI Certificate - Stanford"]
  },
  finance_banking: {
    nameKey: "finance_banking",
    tools: ["Bloomberg Terminal AI", "Kensho", "AlphaSense"],
    courses: [{ name: "AI in Finance", url: "https://www.coursera.org/learn/ai-in-finance" }],
    communities: ["FinTech AI LinkedIn", "Quantitative Finance Reddit"],
    certifications: ["CFA AI Certificate", "Bloomberg Market Concepts"]
  },
  education: {
    nameKey: "education",
    tools: ["Gradescope", "Turnitin AI", "Khan Academy AI", "Duolingo"],
    courses: [{ name: "AI in Education", url: "https://www.coursera.org/learn/ai-education" }],
    communities: ["ISTE AI Network", "EdTech LinkedIn"],
    certifications: ["Google Certified Educator", "Microsoft Innovative Educator"]
  },
  retail: {
    nameKey: "retail",
    tools: ["Shopify AI", "Dynamic Yield", "Salesforce Einstein"],
    courses: [{ name: "AI for Retail", url: "https://www.linkedin.com/learning/artificial-intelligence-for-retail" }],
    communities: ["Retail AI Council", "NRF Tech Community"],
    certifications: ["Salesforce AI Associate"]
  },
  manufacturing: {
    nameKey: "manufacturing",
    tools: ["Siemens Industrial AI", "Uptake", "Sight Machine"],
    courses: [{ name: "AI in Manufacturing", url: "https://www.coursera.org/learn/ai-manufacturing" }],
    communities: ["Smart Manufacturing LinkedIn", "Industry 4.0 Forum"],
    certifications: ["AWS Industrial IoT", "Siemens Digital Industries"]
  },
  creative_design: {
    nameKey: "creative_design",
    tools: ["Adobe Firefly", "Midjourney", "DALL-E", "Runway ML", "Canva AI"],
    courses: [{ name: "Generative AI for Creatives", url: "https://www.linkedin.com/learning/generative-ai-for-creatives" }],
    communities: ["AI Art Reddit", "Creative AI Discord"],
    certifications: ["Adobe Creative AI Badge"]
  },
  professional_services: {
    nameKey: "professional_services",
    tools: ["Harvey AI", "Casetext", "Luminance", "Kira Systems"],
    courses: [{ name: "AI for Legal Professionals", url: "https://www.coursera.org/learn/ai-law" }],
    communities: ["Legal Tech LinkedIn", "ACC Legal Ops"],
    certifications: ["Legal Tech Certificate"]
  },
  other: {
    nameKey: "other",
    tools: ["ChatGPT", "Claude", "Gemini", "Copilot"],
    courses: [{ name: "AI For Everyone", url: "https://www.coursera.org/learn/ai-for-everyone" }],
    communities: ["AI LinkedIn Groups", "Reddit AI Communities"],
    certifications: ["Google AI Essentials", "Microsoft AI Fundamentals"]
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ActionPlan = () => {
  const { t, i18n } = useTranslation('actionPlan');
  const { t: tCommon } = useTranslation('common');
  const { hasSubscription, user } = useAuth();
  
  const [expandedWeek, setExpandedWeek] = useState(1);
  const [completedTasks, setCompletedTasks] = useState({});
  const [userIndustry, setUserIndustry] = useState('other');
  const [userRiskLevel, setUserRiskLevel] = useState('MODERATE');

  const isRTL = i18n.dir() === 'rtl';

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load completed tasks from Supabase if logged in
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('completed_tasks')
            .eq('id', user.id)
            .single();
          
          if (profile?.completed_tasks) {
            setCompletedTasks(profile.completed_tasks);
          }
        } else {
          // Fallback to localStorage for non-logged-in users
          const saved = localStorage.getItem('lab2040_completed_tasks');
          if (saved) setCompletedTasks(JSON.parse(saved));
        }
        
        const results = localStorage.getItem('lab2040_assessment_results');
        const answers = localStorage.getItem('lab2040_assessment_answers');
        
        if (results) {
          const parsed = JSON.parse(results);
          setUserRiskLevel(parsed.riskLevel || 'MODERATE');
        }
        if (answers) {
          const parsed = JSON.parse(answers);
          setUserIndustry(parsed.S2 || 'other');
        }
      } catch (e) {
        console.error('Error loading data:', e);
      }
    };
    
    loadData();
  }, [user]);

  const isPro = typeof hasSubscription === 'function' ? hasSubscription() : false;
  const industryData = INDUSTRY_RESOURCES[userIndustry] || INDUSTRY_RESOURCES.other;

  // Generate phases using translation keys
  const generatePhases = () => {
    const phases = [
      { 
        key: 'phase1', 
        title: t('phases.phase1'), 
        subtitle: t('phases.phase1Subtitle'),
        weeks: [1, 2, 3, 4]
      },
      { 
        key: 'phase2', 
        title: t('phases.phase2'), 
        subtitle: t('phases.phase2Subtitle'),
        weeks: [5, 6, 7, 8]
      },
      { 
        key: 'phase3', 
        title: t('phases.phase3'), 
        subtitle: t('phases.phase3Subtitle'),
        weeks: [9, 10, 11, 12]
      }
    ];
    return phases;
  };

  const getWeekData = (weekNum) => {
    const weekKey = `week${weekNum}`;
    return {
      week: weekNum,
      title: t(`${weekKey}.title`),
      goal: t(`${weekKey}.goal`),
      outcome: t(`${weekKey}.outcome`),
      days: [
        { day: t('days.monday'), task: t(`${weekKey}.day1`), action: t(`${weekKey}.day1Action`), type: 'learning' },
        { day: t('days.tuesday'), task: t(`${weekKey}.day2`), action: t(`${weekKey}.day2Action`), type: 'hands-on' },
        { day: t('days.wednesday'), task: t(`${weekKey}.day3`), action: t(`${weekKey}.day3Action`), type: 'hands-on' },
        { day: t('days.thursday'), task: t(`${weekKey}.day4`), action: t(`${weekKey}.day4Action`), type: 'research' },
        { day: t('days.friday'), task: t(`${weekKey}.day5`), action: t(`${weekKey}.day5Action`), type: 'reflection' }
      ],
      weekend: { task: t(`${weekKey}.weekend`), description: t(`${weekKey}.weekendDesc`) }
    };
  };

  const phases = generatePhases();
  
  // Calculate total tasks (5 days + 1 weekend per week * 12 weeks = 72)
  const totalTasks = 72;
  const completedCount = Object.values(completedTasks).filter(Boolean).length;
  const progressPercent = Math.round((completedCount / totalTasks) * 100) || 0;

  const toggleTask = async (taskId) => {
    const updated = { ...completedTasks, [taskId]: !completedTasks[taskId] };
    setCompletedTasks(updated);
    
    try {
      // Save to localStorage as backup
      localStorage.setItem('lab2040_completed_tasks', JSON.stringify(updated));
      
      // Save to Supabase if logged in
      if (user) {
        await supabase
          .from('profiles')
          .update({ completed_tasks: updated })
          .eq('id', user.id);
      }
    } catch (e) {
      console.error('Error saving tasks:', e);
    }
  };

  const getIcon = (type) => {
    const icons = {
      'learning': <BookOpen className="w-4 h-4 text-blue-500" />,
      'hands-on': <Wrench className="w-4 h-4 text-green-500" />,
      'networking': <Users className="w-4 h-4 text-purple-500" />,
      'research': <Brain className="w-4 h-4 text-orange-500" />,
      'reflection': <Target className="w-4 h-4 text-pink-500" />,
      'planning': <Calendar className="w-4 h-4 text-teal-500" />,
      'business': <TrendingUp className="w-4 h-4 text-emerald-500" />
    };
    return icons[type] || <Circle className="w-4 h-4 text-gray-400" />;
  };

  const getRiskLabel = (level) => {
    const labels = {
      CRITICAL: tCommon('riskLevels.critical'),
      HIGH: tCommon('riskLevels.high'),
      MODERATE: tCommon('riskLevels.moderate'),
      LOW: tCommon('riskLevels.low'),
      MINIMAL: tCommon('riskLevels.minimal')
    };
    return labels[level] || level;
  };

  // Non-Pro view
  if (!isPro) {
    return (
      <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="font-serif text-xl font-bold">
              <span className="text-gray-900">Lab</span><span className="text-teal-500">2040</span>
            </Link>
            <LanguageSelector compact />
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Target className="w-10 h-10 text-teal-600" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-gray-900 mb-4">{t('actionPlan.title')}</h1>
          <p className="text-gray-600 mb-8">{t('actionPlan.description')}</p>
          <Link to="/results" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold rounded-xl">
            <Crown className="w-5 h-5" />
            {t('actionPlan.upgradeToAccess')}
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl font-bold">
            <span className="text-gray-900">Lab</span><span className="text-teal-500">2040</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector compact />
            <span className="text-amber-400 text-sm font-medium px-3 py-1 bg-amber-400/10 rounded-full">⭐ {tCommon('subscription.pro')}</span>
            <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 flex items-center gap-1 text-sm">
              <LayoutDashboard className="w-4 h-4" /> {tCommon('nav.dashboard')}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/dashboard" className={`inline-flex items-center gap-2 text-gray-500 hover:text-teal-600 mb-6`}>
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} /> {tCommon('cta.backToDashboard')}
        </Link>

        {/* Hero */}
        <div className="bg-gradient-to-r from-teal-600 via-teal-700 to-gray-900 rounded-3xl p-8 text-white mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Target className="w-7 h-7" />
            </div>
            <div>
              <h1 className="font-serif text-3xl font-bold">{t('actionPlan.title')}</h1>
              <p className="text-white/80">{t('actionPlan.subtitle')}</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80">{progressPercent}% {t('progress.complete')}</span>
              <span className="text-white/80">{t('progress.tasksCompleted', { completed: completedCount, total: totalTasks })}</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>

        {/* Consistency Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900">{t('actionPlan.consistencyMessage')}</h3>
              <p className="text-amber-700">{t('actionPlan.consistencySubtitle')}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            ['12', t('stats.weeks'), 'teal'],
            ['60', t('stats.tasks'), 'green'],
            ['12', t('stats.projects'), 'teal'],
            ['3-5h', t('stats.perWeek'), 'orange']
          ].map(([num, label, color]) => (
            <div key={label} className="bg-white rounded-xl p-4 shadow-sm border text-center">
              <div className={`text-2xl font-bold text-${color}-600`}>{num}</div>
              <div className="text-sm text-gray-500">{label}</div>
            </div>
          ))}
        </div>

        {/* Industry Resources */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-lg font-bold text-gray-900">
                {t('industry.yourIndustry')}: {tCommon(`questions.S2.options.${industryData.nameKey}`, industryData.nameKey)}
              </h3>
              <p className="text-sm text-amber-700">{t('industry.personalizedResources')}</p>
            </div>
            <span className={`ms-auto px-3 py-1 rounded-full text-xs font-bold ${
              userRiskLevel === 'CRITICAL' || userRiskLevel === 'HIGH' ? 'bg-red-100 text-red-700' :
              userRiskLevel === 'LOW' || userRiskLevel === 'MINIMAL' ? 'bg-green-100 text-green-700' :
              'bg-amber-100 text-amber-700'
            }`}>
              {getRiskLabel(userRiskLevel)} {tCommon('riskLevels.risk')}
            </span>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/70 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-amber-600" /> {t('industry.industryAiTools')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {industryData.tools.map((tool, i) => (
                  <span key={i} className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-sm">{tool}</span>
                ))}
              </div>
            </div>
            <div className="bg-white/70 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-amber-600" /> {t('industry.recommendedCourse')}
              </h4>
              {industryData.courses[0] && (
                <a href={industryData.courses[0].url} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:text-amber-900 flex items-center gap-1">
                  {industryData.courses[0].name} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="bg-white/70 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-600" /> {t('industry.communities')}
              </h4>
              <div className="text-sm text-gray-600">{industryData.communities.join(', ')}</div>
            </div>
            <div className="bg-white/70 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-600" /> {t('industry.certifications')}
              </h4>
              <div className="text-sm text-gray-600">{industryData.certifications.join(', ')}</div>
            </div>
          </div>
        </div>

        {/* Phases */}
        {phases.map((phase, pi) => (
          <div key={pi} className="space-y-4 mb-8">
            <div className={`rounded-2xl p-6 text-white ${
              pi === 0 ? 'bg-gradient-to-r from-teal-600 to-teal-700' : 
              pi === 1 ? 'bg-gradient-to-r from-teal-700 to-gray-800' : 
              'bg-gradient-to-r from-gray-800 to-gray-900'
            }`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">{pi + 1}</div>
                <div>
                  <h3 className="font-serif text-xl font-bold">{t('phases.phase')} {pi + 1}: {phase.title}</h3>
                  <p className="text-white/80 text-sm">{t('weeks.week')} {phase.weeks[0]}-{phase.weeks[3]} • {phase.subtitle}</p>
                </div>
              </div>
            </div>

            {phase.weeks.map((weekNum) => {
              const week = getWeekData(weekNum);
              const weekId = `week-${weekNum}`;
              const isExpanded = expandedWeek === weekNum;
              const weekTaskCount = 6; // 5 days + 1 weekend
              const weekDone = week.days.filter((_, i) => completedTasks[`${weekId}-day-${i}`]).length + (completedTasks[`${weekId}-weekend`] ? 1 : 0);

              return (
                <div key={weekNum} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                  <button 
                    onClick={() => setExpandedWeek(isExpanded ? null : weekNum)} 
                    className="w-full p-5 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold ${
                        weekDone === weekTaskCount ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {weekDone === weekTaskCount ? <CheckCircle className="w-6 h-6" /> : `W${weekNum}`}
                      </div>
                      <div className="text-start">
                        <h4 className="font-semibold text-gray-900">{t('weeks.weekNumber', { number: weekNum })}: {week.title}</h4>
                        <p className="text-sm text-gray-500">{week.goal}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-end">
                        <span className="text-sm text-gray-500">{weekDone}/{weekTaskCount}</span>
                      </div>
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t p-5 space-y-4">
                      <div className="bg-teal-50 rounded-xl p-4">
                        <p className="text-sm text-teal-600 font-medium">{t('weeks.keyOutcome')}</p>
                        <p className="text-teal-900">{week.outcome}</p>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-700 mb-3">{t('weeks.dailyTasks')} ({t('weeks.eachTaskTime')})</h5>
                        <div className="space-y-2">
                          {week.days.map((day, di) => {
                            const taskId = `${weekId}-day-${di}`;
                            const done = completedTasks[taskId];
                            return (
                              <button
                                key={di}
                                onClick={() => toggleTask(taskId)}
                                className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-start ${
                                  done ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-teal-300'
                                }`}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  done ? 'bg-green-500 text-white' : 'border-2 border-gray-300'
                                }`}>
                                  {done && <CheckCircle className="w-4 h-4" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    {getIcon(day.type)}
                                    <span className="font-medium text-gray-900">{day.day}</span>
                                  </div>
                                  <p className={`text-sm ${done ? 'text-gray-500 line-through' : 'text-gray-700'}`}>{day.task}</p>
                                  <p className="text-xs text-gray-400 mt-1">→ {day.action}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <h5 className="font-medium text-gray-700 mb-3">{t('weeks.weekendChallenge')}</h5>
                        <button
                          onClick={() => toggleTask(`${weekId}-weekend`)}
                          className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-start ${
                            completedTasks[`${weekId}-weekend`] 
                              ? 'bg-teal-50 border-teal-300' 
                              : 'bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200 hover:border-teal-400'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            completedTasks[`${weekId}-weekend`] ? 'bg-teal-500 text-white' : 'bg-teal-200 text-teal-600'
                          }`}>
                            {completedTasks[`${weekId}-weekend`] ? <CheckCircle className="w-5 h-5" /> : <Trophy className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="font-semibold text-teal-900">{week.weekend.task}</p>
                            <p className="text-sm text-teal-700">{week.weekend.description}</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </main>
    </div>
  );
};

export default ActionPlan;
